import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { TFillRef } from "@libs/figure-spans";
import { Callout, CalloutContent, CalloutLabel } from "@libs/zapi-ui/ds/components/callout";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { TProjectOverview } from "@/zapi-frontend/stores/project";
import { refsAt } from "@/zapi-frontend/shared/lib/figureRefs";
import { FigureText } from "../../../MemoIsland/FigureRefs/FigureTrigger";
import { HomeWidget } from "./HomeWidget";

function HomeSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h4 className="typo-overline text-muted-foreground">{label}</h4>
      {children}
    </section>
  );
}

function ReadinessPill({ score, total }: { score: number; total: number }) {
  const { t } = useTranslation("projectHome");
  const ready = total > 0 && score === total;
  return (
    <span
      className={cn(
        "typo-label-sm flex h-6 items-center gap-1.5 rounded-full px-2.5 tabular-nums",
        ready ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"
      )}
    >
      <span className={cn("size-1.5 rounded-full", ready ? "bg-success" : "bg-primary-500")} aria-hidden />
      {t("overview.criteriaMet", { score, total })}
    </span>
  );
}

type TOpportunityFrameBlockProps = {
  overview: TProjectOverview;
  decisionStatus?: string | null;
  decisionStatusRefs?: TFillRef[] | null;
};

export function OpportunityFrameBlock({ overview, decisionStatus, decisionStatusRefs }: TOpportunityFrameBlockProps) {
  const { t } = useTranslation("projectHome");
  const { question, verdict } = overview.opportunityFrame;
  const { items } = overview.successCriteria;
  if (!question && !verdict && !decisionStatus) return null;

  return (
    <HomeWidget
      title={t("overview.whereItStands")}
      bodyClassName="gap-5"
      actions={
        items.length > 0 && (
          <ReadinessPill score={items.filter((item) => item.status === "pass").length} total={items.length} />
        )
      }
    >
      {question && (
        <HomeSection label={t("overview.question")}>
          <p className="typo-body-sm text-muted-foreground">
            <FigureText text={question} refs={refsAt(overview, "opportunityFrame.question")} />
          </p>
        </HomeSection>
      )}
      {verdict && (
        <Callout size="lg">
          <CalloutLabel>{t("overview.verdict")}</CalloutLabel>
          <CalloutContent>
            <FigureText text={verdict} refs={refsAt(overview, "opportunityFrame.verdict")} />
          </CalloutContent>
        </Callout>
      )}
      <div className="border-t pt-4">
        <HomeSection label={t("overview.recommendation")}>
          {decisionStatus ? (
            <p className="typo-body-sm text-foreground">
              <FigureText text={decisionStatus} refs={decisionStatusRefs} />
            </p>
          ) : (
            <p className="typo-body-sm text-muted-foreground">{t("overview.recommendationPending")}</p>
          )}
        </HomeSection>
      </div>
    </HomeWidget>
  );
}
